import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Paper, Alert, CircularProgress } from '@mui/material';
import ScheduledClassForm from '../components/ScheduledClassForm';
import api, { handleApiError, translateValidationDetail } from '../utils/api';

const EditScheduledClassPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [scheduledClass, setScheduledClass] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadScheduledClass = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/scheduled-classes/${id}`);

      if (response.data.success) {
        setScheduledClass(response.data.data);
      }
    } catch (error) {
      console.error('Error loading scheduled class:', error);
      setError(t('admin:scheduledClasses.editPage.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    loadScheduledClass();
  }, [loadScheduledClass]);

  const handleSubmit = async (formData) => {
    try {
      const response = await api.put(`/scheduled-classes/${id}`, formData);

      if (response.data.success) {
        navigate('/admin/scheduled-classes', {
          state: {
            message: t('admin:scheduledClasses.editPage.success.updated'),
            severity: 'success'
          }
        });
      }
    } catch (error) {
      console.error('Error updating scheduled class:', error);
      console.error('Validation details:', error.response?.data?.details);

      // Get translated error message
      const translatedError = handleApiError(error);

      // Log details if available
      const details = error.response?.data?.details;
      if (details && Array.isArray(details)) {
        details.forEach((detail, index) => {
          console.error(`Validation error ${index + 1}:`, detail);
        });

        // Translate detail messages using the helper function
        const translatedDetails = details.map(translateValidationDetail);

        const detailsText = translatedDetails.join(', ');
        throw new Error(`${translatedError}: ${detailsText}`);
      }

      throw new Error(translatedError);
    }
  };

  const handleCancel = () => {
    navigate('/admin/scheduled-classes');
  };

  if (loading) {
    return (
      <Box sx={{ maxWidth: 1000, mx: 'auto', p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 1000, mx: 'auto', p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!scheduledClass) {
    return (
      <Box sx={{ maxWidth: 1000, mx: 'auto', p: 3 }}>
        <Alert severity="error">{t('admin:scheduledClasses.editPage.errors.notFound')}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: 3 }}>
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {t('admin:scheduledClasses.editPage.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {t('admin:scheduledClasses.editPage.description')}
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          <strong>{t('admin:scheduledClasses.editPage.planAccessInfo')}</strong>
        </Alert>
      </Paper>

      <ScheduledClassForm
        initialData={scheduledClass}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
      />
    </Box>
  );
};

export default EditScheduledClassPage;